"use client";

import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { AppLocale } from "@/core/i18n/config";
import { normalizeAppError } from "@/shared/lib/async-result";
import { SESSION_TYPE_OPTIONS } from "../../../constants/session-types";
import { CONCERN_FORM_DEFAULTS, validateConcernForm, type ConcernFormValues } from "../../../schemas/concern.schema";
import { submitConcern } from "../../../data/repositories/booking.repository";
import { useBookingStepperStore } from "../../states/booking-stepper.store";
const LANGUAGE_OPTIONS = ["English", "Hindi", "Other"];
const RELATIONSHIP_STATUS_OPTIONS = [{
  value: "single",
  en: "Single",
  hi: "अविवाहित"
}, {
  value: "in_a_relationship",
  en: "In a relationship",
  hi: "रिश्ते में"
}, {
  value: "married",
  en: "Married",
  hi: "विवाहित"
}, {
  value: "separated",
  en: "Separated/Divorced",
  hi: "अलग/तलाकशुदा"
}, {
  value: "other",
  en: "Other",
  hi: "अन्य"
}];
const URGENCY_OPTIONS: {
  value: ConcernFormValues["urgencyLevel"];
  en: string;
  hi: string;
}[] = [{
  value: "low",
  en: "Low",
  hi: "कम"
}, {
  value: "medium",
  en: "Medium",
  hi: "मध्यम"
}, {
  value: "high",
  en: "High",
  hi: "उच्च"
}];
function getErrorMessage(error: unknown): string {
  return normalizeAppError(error).message;
}
export function Step4ConcernForm({
  locale
}: {
  locale: AppLocale;
}) {
  const isHindi = locale === "hi";
  const sessionType = useBookingStepperStore(state => state.sessionType);
  const holdId = useBookingStepperStore(state_0 => state_0.holdId);
  const setConcern = useBookingStepperStore(state_1 => state_1.setConcern);
  const previousStep = useBookingStepperStore(state_2 => state_2.previousStep);
  const [values, setValues] = useState<ConcernFormValues>({
    ...CONCERN_FORM_DEFAULTS,
    concernCategory: sessionType ?? CONCERN_FORM_DEFAULTS.concernCategory
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ConcernFormValues, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  function update<K extends keyof ConcernFormValues>(key: K, value: ConcernFormValues[K]) {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
  }
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = validateConcernForm(values);
    if (!result.success) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    if (!holdId) {
      toast.error(isHindi ? "स्लॉट होल्ड नहीं मिला। कृपया फिर से समय चुनें।" : "No active slot hold found. Please choose a time again.");
      return;
    }
    setIsSubmitting(true);
    try {
      await submitConcern(holdId, result.data);
      setConcern(result.data);
    } catch (caughtError) {
      toast.error(getErrorMessage(caughtError));
    } finally {
      setIsSubmitting(false);
    }
  }
  const inputClass = "mt-1.5 w-full rounded-lg border border-[#EAE5DA] px-3 py-2.5 text-sm focus:border-[#155A7D] focus:outline-none";
  const labelClass = "text-xs font-semibold text-slate-600";
  const errorClass = "mt-1 text-xs text-red-600";
  return <form onSubmit={handleSubmit}>
      <h2 className="font-heading text-xl font-semibold text-[#0E2A3A]">
        {isHindi ? "अपनी चिंता के बारे में बताएं" : "Tell us about your concern"}
      </h2>

      <p className="mt-2 text-sm text-slate-500">
        {isHindi ? "आपकी प्रतिक्रियाएं निजी हैं और केवल आपके सत्र की तैयारी में मदद करने के लिए आपके नियुक्त हीलर के साथ साझा की जाती हैं।" : "Your responses are private and only shared with your assigned healer to help them prepare for your session."}
      </p>

      <div className="mt-5 flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-xs leading-5 text-amber-800">
          {isHindi ? "ItsHealer एक आपातकालीन सेवा नहीं है। यदि आप असुरक्षित या तत्काल खतरे में महसूस करते हैं, तो स्थानीय आपातकालीन सेवाओं या किसी विश्वसनीय व्यक्ति से संपर्क करें।" : "ItsHealer is not an emergency service. If you feel unsafe or in immediate danger, contact local emergency services or someone you trust."}
        </p>
      </div>

      <div className="mt-6 space-y-5">
        <div>
          <label className={labelClass}>
            {isHindi ? "आप किस बारे में बात करना चाहेंगे?" : "What would you like to discuss?"}
          </label>
          <textarea value={values.discussTopic} onChange={event_0 => update("discussTopic", event_0.target.value)} rows={4} className={inputClass} placeholder={isHindi ? "यहां लिखें..." : "Share what's on your mind..."} />
          {errors.discussTopic && <p className={errorClass}>{errors.discussTopic}</p>}
        </div>

        <div>
          <label className={labelClass}>{isHindi ? "श्रेणी" : "Category"}</label>
          <select value={values.concernCategory} onChange={event_1 => update("concernCategory", event_1.target.value as ConcernFormValues["concernCategory"])} className={inputClass}>
            {SESSION_TYPE_OPTIONS.map(option => <option key={option.value} value={option.value}>
                {isHindi ? option.hi : option.en}
              </option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>
              {isHindi ? "रिश्ते की स्थिति" : "Relationship status"}
            </label>
            <select value={values.relationshipStatus} onChange={event_2 => update("relationshipStatus", event_2.target.value)} className={inputClass}>
              <option value="">{isHindi ? "चुनें" : "Select"}</option>
              {RELATIONSHIP_STATUS_OPTIONS.map(option_0 => <option key={option_0.value} value={option_0.value}>
                  {isHindi ? option_0.hi : option_0.en}
                </option>)}
            </select>
            {errors.relationshipStatus && <p className={errorClass}>{errors.relationshipStatus}</p>}
          </div>

          <div>
            <label className={labelClass}>{isHindi ? "पसंदीदा भाषा" : "Preferred language"}</label>
            <select value={values.preferredLanguage} onChange={event_3 => update("preferredLanguage", event_3.target.value)} className={inputClass}>
              <option value="">{isHindi ? "चुनें" : "Select"}</option>
              {LANGUAGE_OPTIONS.map(option_1 => <option key={option_1} value={option_1}>
                  {option_1}
                </option>)}
            </select>
            {errors.preferredLanguage && <p className={errorClass}>{errors.preferredLanguage}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>
              {isHindi ? "यह सत्र किसके लिए है?" : "Booking for"}
            </label>
            <div className="mt-1.5 flex gap-2">
              {(["self", "couple"] as const).map(value_0 => <button key={value_0} type="button" onClick={() => update("bookingFor", value_0)} className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition ${values.bookingFor === value_0 ? "border-[#155A7D] bg-[#155A7D]/10 text-[#155A7D]" : "border-[#EAE5DA] text-slate-600"}`}>
                  {value_0 === "self" ? isHindi ? "स्वयं" : "Self" : isHindi ? "जोड़ा" : "Couple"}
                </button>)}
            </div>
          </div>

          <div>
            <label className={labelClass}>{isHindi ? "तात्कालिकता" : "Urgency"}</label>
            <div className="mt-1.5 flex gap-2">
              {URGENCY_OPTIONS.map(option_2 => <button key={option_2.value} type="button" onClick={() => update("urgencyLevel", option_2.value)} className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition ${values.urgencyLevel === option_2.value ? "border-[#155A7D] bg-[#155A7D]/10 text-[#155A7D]" : "border-[#EAE5DA] text-slate-600"}`}>
                  {isHindi ? option_2.hi : option_2.en}
                </button>)}
            </div>
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={values.isFirstSession} onChange={event_4 => update("isFirstSession", event_4.target.checked)} className="h-4 w-4 rounded border-[#EAE5DA] text-[#155A7D] focus:ring-[#155A7D]" />
            {isHindi ? "यह मेरा पहला सत्र है" : "This is my first session"}
          </label>
        </div>

        <div>
          <label className={labelClass}>
            {isHindi ? "अतिरिक्त नोट्स (वैकल्पिक)" : "Additional notes (optional)"}
          </label>
          <textarea value={values.additionalNotes} onChange={event_5 => update("additionalNotes", event_5.target.value)} rows={3} className={inputClass} />
        </div>
      </div>

      <div className="mt-8 flex gap-3">
        <button type="button" onClick={previousStep} className="rounded-full border border-[#EAE5DA] px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
          {isHindi ? "वापस" : "Back"}
        </button>
        <button type="submit" disabled={isSubmitting} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#155A7D] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0E4663] disabled:cursor-not-allowed disabled:opacity-60">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {isHindi ? "जारी रखें" : "Continue"}
        </button>
      </div>
    </form>;
}